/* Les fondations : les valeurs qui existent, et rien d'autre.
 *
 * Tout ce qui est montré ici est lu dans la feuille de style au moment du
 * rendu. Si une marche disparaît demain, la ligne devient vide — et c'est le
 * comportement voulu : une charte qui continue d'afficher une valeur retirée
 * est pire qu'une charte absente.
 */

import { Section, Tag } from "@/ui/primitives";
import { SEUIL_GRAND } from "@/domain/contraste";
import { A11y, BlocP, BlocT, Contraste, Usage, useJetons } from "./briques";

const ARDOISE = [0, 50, 100, 200, 300, 400, 500, 600, 700, 800, 900];
const NUIT = [50, 100, 200, 300, 400, 500, 600, 700, 800, 850, 900];
const NOCTURNE = [1, 2, 3, 4, 5, 6, 7, 8, 9];

const SEMANTIQUE: { nom: string; clair: string; sombre: string; quoi: string }[] = [
  { nom: "--bg", clair: "ardoise-100", sombre: "nocturne-1", quoi: "le fond de la page" },
  { nom: "--surface", clair: "ardoise-0", sombre: "nocturne-2", quoi: "une carte, un tiroir, une ligne" },
  { nom: "--surface-2", clair: "ardoise-50", sombre: "nocturne-3", quoi: "un champ, une zone en retrait" },
  { nom: "--surface-3", clair: "ardoise-200", sombre: "nocturne-4", quoi: "un survol, une pastille" },
  { nom: "--line", clair: "ardoise-300", sombre: "nocturne-5", quoi: "séparer sans découper" },
  { nom: "--line-2", clair: "ardoise-400", sombre: "nocturne-6", quoi: "le contour d'un champ" },
  { nom: "--ink", clair: "ardoise-900", sombre: "nocturne-9", quoi: "le texte qui porte" },
  { nom: "--ink-2", clair: "ardoise-700", sombre: "nocturne-8", quoi: "le texte secondaire" },
  { nom: "--ink-3", clair: "ardoise-600", sombre: "nocturne-7", quoi: "la mention" },
  { nom: "--accent", clair: "nuit-800", sombre: "nuit-300", quoi: "l'action principale" },
  { nom: "--accent-soft", clair: "pierre", sombre: "nuit-850", quoi: "l'aplat de l'accent" },
  { nom: "--on-accent", clair: "ardoise-0", sombre: "nocturne-1", quoi: "ce qu'on écrit dessus" },
];

const TYPO: { jeton: string; px: string; quoi: string; classe?: string }[] = [
  { jeton: "--t-micro", px: "10", quoi: "compteur, code court", classe: "nav-count" },
  { jeton: "--t-mini", px: "11", quoi: "en-tête de colonne, étiquette mono", classe: "kpi-k" },
  { jeton: "--t-mention", px: "12", quoi: "métadonnée, sous-ligne" },
  { jeton: "--t-second", px: "13", quoi: "corps secondaire, cellule" },
  { jeton: "--t-corps", px: "14", quoi: "corps de l'interface" },
  { jeton: "--t-fort", px: "16", quoi: "titre de section ; plancher d'un champ au téléphone" },
  { jeton: "--t-titre", px: "19", quoi: "titre d'écran", classe: "view-title" },
  { jeton: "--t-chiffre", px: "24", quoi: "la valeur d'un indicateur", classe: "kpi-v" },
];

const ESPACES = ["--e-1", "--e-2", "--e-3", "--e-4", "--e-5", "--e-6", "--e-7", "--e-8", "--e-9", "--e-10"];
const RAYONS = ["--r-0", "--r-1", "--r-2", "--r-3", "--r-4", "--r-plein"];
const COUCHES = ["--z-colle", "--z-barre", "--z-rail", "--z-voile", "--z-tiroir", "--z-onglets", "--z-feuille", "--z-toast"];

const ETATS = [
  { nom: "--ok", soft: "--ok-soft", ton: "ok", quoi: "fait, payé, conforme" },
  { nom: "--warn", soft: "--warn-soft", ton: "warn", quoi: "attend, approche" },
  { nom: "--crit", soft: "--crit-soft", ton: "crit", quoi: "en retard, refusé" },
  { nom: "--info", soft: "--info-soft", ton: "info", quoi: "neutre à signaler" },
  { nom: "--violet", soft: "--violet-soft", ton: "violet", quoi: "le packaging" },
  { nom: "--ciel", soft: "--ciel-soft", ton: "ciel", quoi: "la qualité" },
];

function Rampe({
  prefixe, marches, lire, surQuoi,
}: { prefixe: string; marches: (number | string)[]; lire: (n: string) => string; surQuoi: string }) {
  return (
    <div className="rampe">
      {marches.map((m) => {
        const jeton = `--${prefixe}-${m}`;
        return (
          <div className="marche" key={jeton}>
            <span className="marche-eau" style={{ background: `var(${jeton})` }} />
            <span className="marche-n">{m}</span>
            <span className="marche-v mono">{lire(jeton) || "—"}</span>
            <Usage avant={jeton} arriere={surQuoi} lire={lire} />
          </div>
        );
      })}
    </div>
  );
}

export function Fondations() {
  const lire = useJetons();

  return (
    <>
      <Section titre="Marque" note="deux couleurs, qui ne suivent pas le thème">
        <div className="card card-pad">
          <div className="marque">
            <div className="marque-c" style={{ background: "var(--nuit)", color: "var(--ardoise-0)" }}>
              <b>Nuit</b>
              <span className="mono">{lire("--nuit")}</span>
              <span>Signe la capsule, les boutons, les bandeaux. C'est la marche 800 de sa propre rampe — la couleur de la marque n'est pas à côté du système, elle en fait partie.</span>
            </div>
            <div className="marque-c" style={{ background: "var(--pierre)", color: "var(--nuit)" }}>
              <b>Pierre</b>
              <span className="mono">{lire("--pierre")}</span>
              <span>Le ton clair de la marque. Aucune règle ne l'emploie directement : il passe par <code>--accent-soft</code>, qui porte la même valeur.</span>
            </div>
          </div>
        </div>
      </Section>

      <Section titre="Rampes" note="trois, et aucune couleur en dehors">
        <div className="card card-pad">
          <BlocT note="la neutre, teinte 219 d'un bout à l'autre">Ardoise · thème clair</BlocT>
          <BlocP>
            Un gris qui tire vers le bleu de la marque plutôt que vers le vert. Contraste indiqué
            sur blanc.
          </BlocP>
          <Rampe prefixe="ardoise" marches={ARDOISE} lire={lire} surQuoi="--ardoise-0" />

          <BlocT note="la marque, déclinée">Nuit</BlocT>
          <BlocP>
            La marche 850 n'est pas un caprice : en thème sombre il faut une surface teintée nuit
            qui se détache du fond, et la 900 a presque la luminance exacte du fond sombre —
            rapport 1,01, autant dire invisible.
          </BlocP>
          <Rampe prefixe="nuit" marches={NUIT} lire={lire} surQuoi="--ardoise-0" />

          <BlocT note="le thème sombre a ses propres marches">Nocturne</BlocT>
          <BlocP>
            Le sombre n'est pas la rampe claire inversée : inverser donne des surfaces boueuses,
            parce que l'œil ne lit pas le noir comme le blanc. Contraste indiqué sur le fond
            sombre.
          </BlocP>
          <Rampe prefixe="nocturne" marches={NOCTURNE} lire={lire} surQuoi="--nocturne-1" />
        </div>
      </Section>

      <Section titre="Sémantique" note="ce que les règles ont le droit de nommer">
        <div className="card card-pad">
          <BlocP>
            Les règles n'emploient jamais une marche de rampe directement. Elles nomment un rôle,
            et c'est cette couche qui dit à quelle marche il correspond — c'est elle, et elle
            seule, que le thème réaffecte.
          </BlocP>
          <div className="tablewrap">
            <table className="api">
              <thead>
                <tr>
                  <th>Rôle</th>
                  <th>Clair</th>
                  <th>Sombre</th>
                  <th />
                  <th>Emploi</th>
                </tr>
              </thead>
              <tbody>
                {SEMANTIQUE.map((s) => (
                  <tr key={s.nom}>
                    <td><code>{s.nom}</code></td>
                    <td><code className="api-t">{s.clair}</code></td>
                    <td><code className="api-t">{s.sombre}</code></td>
                    <td>
                      <span className="eau-mini" style={{ background: `var(${s.nom})` }} />
                    </td>
                    <td>{s.quoi}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <BlocT note="teinte pleine pour le texte, aplat doux pour le fond">États</BlocT>
          <BlocP>
            Les six paires sont vérifiées l'une contre l'autre. On ne croise jamais deux paires :
            le contraste d'un croisement n'est garanti par personne.
          </BlocP>
          <div className="jetons">
            {ETATS.map((e) => (
              <div className="jeton" key={e.nom}>
                <span
                  className="jeton-eau"
                  style={{ background: `var(${e.soft})`, color: `var(${e.nom})` }}
                >
                  Aa
                </span>
                <span className="jeton-txt">
                  <code className="jeton-nom">{e.nom} · {e.soft}</code>
                  <span className="jeton-quoi">{e.quoi}</span>
                </span>
                <Tag ton={e.ton}>{e.ton}</Tag>
                <Contraste avant={e.nom} arriere={e.soft} lire={lire} />
              </div>
            ))}
          </div>
          <A11y>
            Chaque paire tient 4,5 au moins, dans les deux thèmes, et une épreuve le vérifie sur
            la feuille de style elle-même à chaque livraison. Seule <code>--ink-3</code> reste au
            seuil du grand texte ({String(SEUIL_GRAND)}) : c'est un choix, elle ne porte jamais
            seule une information.
          </A11y>
        </div>
      </Section>

      <Section titre="Typographie" note="huit marches, deux familles">
        <div className="card card-pad">
          <div className="echelle-t">
            {TYPO.map((t) => (
              <div className="et" key={t.jeton}>
                <span className={`et-ex ${t.classe ?? ""}`} style={{ fontSize: `var(${t.jeton})` }}>
                  Devis SO-0042
                </span>
                <code className="et-j">{t.jeton}</code>
                <span className="et-v mono">{lire(t.jeton) || `${t.px}px`}</span>
                <span className="et-q">{t.quoi}</span>
              </div>
            ))}
          </div>
          <BlocP>
            Elles ont remplacé dix-huit tailles accumulées. Au-delà de huit marches, personne ne
            choisit plus : chacun approche, et l'écart se voit. Geist porte l'interface, JetBrains
            Mono tout ce qui s'aligne ou se compare — si l'on peut vouloir lire deux valeurs l'une
            sous l'autre, elles sont en chasse fixe.
          </BlocP>
          <div className="mesures">
            {["--lh-serre", "--lh-corps", "--lh-aere"].map((l) => (
              <div className="mesure" key={l}>
                <code className="jeton-nom">{l}</code>
                <span className="jeton-val mono">{lire(l)}</span>
                <span className="jeton-quoi">
                  {l === "--lh-serre" ? "titres et chiffres" : l === "--lh-corps" ? "texte courant" : "paragraphe long"}
                </span>
              </div>
            ))}
          </div>
        </div>
      </Section>

      <Section titre="Espacement" note="base 4, dix marches">
        <div className="card card-pad">
          <div className="echelle-e">
            {ESPACES.map((e) => (
              <div className="ee" key={e}>
                <code className="jeton-nom">{e}</code>
                <span className="ee-barre" style={{ width: `var(${e})` }} />
                <span className="jeton-val mono">{lire(e)}</span>
              </div>
            ))}
          </div>
          <BlocP>
            Tout écart posé à partir d'aujourd'hui se prend ici. Les règles anciennes en portent
            encore qui n'y sont pas — trop pour être reprises d'un coup sans décaler toute
            l'interface. Plutôt que de prétendre le contraire, le compte est tenu et verrouillé :
            une épreuve refuse qu'il augmente. Il ne peut que descendre, et le jour où il atteint
            zéro, l'échelle devient aussi stricte que les autres.
          </BlocP>
        </div>
      </Section>

      <Section titre="Rayons, élévation, profondeur" note="peu de valeurs, toutes nommées">
        <div className="card card-pad">
          <BlocT>Rayons</BlocT>
          <div className="rayons">
            {RAYONS.map((r) => (
              <div className="ray" key={r}>
                <span className="ray-eau" style={{ borderRadius: `var(${r})` }} />
                <code className="jeton-nom">{r}</code>
                <span className="jeton-val mono">{lire(r)}</span>
              </div>
            ))}
          </div>
          <BlocP>
            Cinq, dont un seul par défaut — <code>--r-2</code>, derrière l'alias historique
            <code> --r</code>. Douze rayons différents, c'étaient douze décisions reprises à chaque
            composant.
          </BlocP>

          <BlocT>Élévation</BlocT>
          <div className="elevations">
            {["--ombre-0", "--ombre-1", "--ombre-2", "--ombre-3"].map((o) => (
              <div className="elev" key={o}>
                <span className="elev-eau" style={{ boxShadow: `var(${o})` }} />
                <code className="jeton-nom">{o}</code>
                <span className="jeton-quoi">
                  {o === "--ombre-0" ? "à plat : la règle" : o === "--ombre-1" ? "une carte qui se décolle"
                    : o === "--ombre-2" ? "un panneau, une feuille" : "ce qui couvre tout le reste"}
                </span>
              </div>
            ))}
          </div>
          <BlocP>
            On ne superpose jamais deux ombres. <code>--ombre-haut</code> existe en plus, pour une
            barre collée en bas qui projette vers le haut.
          </BlocP>

          <BlocT>Profondeur</BlocT>
          <div className="couches">
            {COUCHES.map((c) => (
              <div className="couche" key={c}>
                <span className="couche-n mono">{lire(c)}</span>
                <code className="jeton-nom">{c}</code>
              </div>
            ))}
          </div>
          <BlocP>
            Les couches sont nommées, pas numérotées à la main. Un <code>z-index</code> écrit en
            dur est une couche que personne ne peut situer par rapport aux autres — et c'est
            toujours celle-là qui passe derrière le voile au mauvais moment.
          </BlocP>

          <BlocT>Mouvement</BlocT>
          <div className="mesures">
            {["--duree-court", "--duree-moyen", "--duree-long", "--courbe"].map((d) => (
              <div className="mesure" key={d}>
                <code className="jeton-nom">{d}</code>
                <span className="jeton-val mono">{lire(d)}</span>
              </div>
            ))}
          </div>
          <BlocP>
            Court par principe : une interface de travail ne se regarde pas bouger. Et tout
            s'arrête net si le système demande moins d'animation — la règle est posée une fois,
            pour toute la feuille.
          </BlocP>
        </div>
      </Section>
    </>
  );
}
